import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, CheckCircle2, Terminal, Sparkles } from "lucide-react";
import axios from "axios";
import { SERVICES } from "@/constants/testIds";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BUSINESS_TYPES = [
  "",
  "Restaurant",
  "HVAC",
  "Plumbing",
  "Lawn Care",
  "Fencing",
  "Contractor",
  "Professional Services",
  "Small Business",
  "Other",
];

export default function ConsultationModal({ open, onClose, defaultBusiness = "" }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business: defaultBusiness,
    phone: "",
    message: "",
    website_url: "",
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, business: defaultBusiness || f.business }));
      document.body.style.overflow = "hidden";
      return () => (document.body.style.overflow = "");
    }
  }, [open, defaultBusiness]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && open && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please share your name.";
    if (!form.email.trim()) e.email = "Please share your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "That email looks off.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, {
        name: form.name.trim(),
        email: form.email.trim(),
        business: form.business || undefined,
        phone: form.phone.trim() || undefined,
        message: form.message.trim() || undefined,
        source: "services-consultation",
        website_url: form.website_url,
      });
      setSubmitted(true);
    } catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      const msg = Array.isArray(d) ? d[0]?.msg : d;
      if (status === 429) {
        setErrors({ form: msg || "Too many submissions from this network. Please wait a few minutes." });
      } else {
        setErrors({ form: msg || "Something went wrong. Please try again." });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setForm({ name: "", email: "", business: defaultBusiness, phone: "", message: "", website_url: "" });
    setErrors({});
    setSubmitted(false);
  };

  const close = () => {
    onClose?.();
    setTimeout(reset, 250);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid={SERVICES.modalRoot}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-slate-950/85 backdrop-blur-md"
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Card */}
          <motion.div
            className="relative w-full max-w-lg bg-slate-950 border border-cyan-500/40 shadow-[0_0_80px_rgba(6,182,212,0.25)]"
            initial={{ y: 24, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 16, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Corner ticks */}
            <div className="absolute top-0 left-0 h-4 w-4 border-t border-l border-cyan-400" />
            <div className="absolute top-0 right-0 h-4 w-4 border-t border-r border-cyan-400" />
            <div className="absolute bottom-0 left-0 h-4 w-4 border-b border-l border-cyan-400" />
            <div className="absolute bottom-0 right-0 h-4 w-4 border-b border-r border-cyan-400" />

            <button
              data-testid={SERVICES.modalClose}
              onClick={close}
              aria-label="Close"
              className="absolute top-3 right-3 h-9 w-9 flex items-center justify-center text-slate-400 hover:text-cyan-300 hover:bg-slate-900 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="p-7 sm:p-9 max-h-[85vh] overflow-y-auto">
              {!submitted ? (
                <form onSubmit={submit} className="space-y-4" noValidate>
                  {/* Honeypot */}
                  <div
                    aria-hidden="true"
                    className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden opacity-0 pointer-events-none"
                  >
                    <label htmlFor="c-website-url">Website</label>
                    <input
                      id="c-website-url"
                      type="text"
                      name="website_url"
                      tabIndex={-1}
                      autoComplete="off"
                      value={form.website_url}
                      onChange={set("website_url")}
                    />
                  </div>
                  <div className="flex items-center gap-2 mb-3 font-mono text-[11px] uppercase tracking-[0.28em] text-cyan-400">
                    <Terminal className="h-3.5 w-3.5" />
                    <span>/ book.free_consultation</span>
                  </div>
                  <h3 className="font-display text-2xl sm:text-3xl font-black text-white leading-tight">
                    Tell us about your business.
                  </h3>
                  <p className="font-mono text-xs text-slate-400 leading-relaxed">
                    We&apos;ll reply within 1 business day with a plan tailored to your goals.
                  </p>

                  <Field
                    id="c-name"
                    label="Your name *"
                    value={form.name}
                    onChange={set("name")}
                    error={errors.name}
                    testId={SERVICES.modalName}
                    placeholder="Jordan Kim"
                    autoComplete="name"
                  />
                  <Field
                    id="c-email"
                    label="Email *"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    error={errors.email}
                    testId={SERVICES.modalEmail}
                    placeholder="jordan@company.com"
                    autoComplete="email"
                  />

                  <div>
                    <label
                      htmlFor="c-business"
                      className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2"
                    >
                      Business type
                    </label>
                    <select
                      id="c-business"
                      data-testid={SERVICES.modalBusiness}
                      value={form.business}
                      onChange={set("business")}
                      className="w-full bg-slate-900 border border-white/10 text-white h-11 px-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 transition-colors"
                    >
                      {BUSINESS_TYPES.map((b) => (
                        <option key={b} value={b}>
                          {b || "Select a category…"}
                        </option>
                      ))}
                    </select>
                  </div>

                  <Field
                    id="c-phone"
                    label="Phone (optional)"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    testId={SERVICES.modalPhone}
                    placeholder="(555) 123-4567"
                    autoComplete="tel"
                  />

                  <div>
                    <label
                      htmlFor="c-message"
                      className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2"
                    >
                      What are you looking for?
                    </label>
                    <textarea
                      id="c-message"
                      data-testid={SERVICES.modalMessage}
                      value={form.message}
                      onChange={set("message")}
                      rows={3}
                      placeholder="A new website / automating follow-ups / training my team / …"
                      className="w-full bg-slate-900 border border-white/10 text-white placeholder:text-slate-600 px-3 py-2.5 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 transition-colors resize-none"
                    />
                  </div>

                  {errors.form && (
                    <p
                      data-testid={SERVICES.modalFormError}
                      className="font-mono text-xs text-red-400"
                    >
                      {errors.form}
                    </p>
                  )}

                  <button
                    type="submit"
                    data-testid={SERVICES.modalSubmit}
                    disabled={submitting}
                    className="w-full group inline-flex items-center justify-center gap-3 bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 disabled:cursor-not-allowed text-slate-950 font-bold font-mono uppercase tracking-[0.18em] text-sm px-8 py-4 shadow-[0_0_28px_rgba(6,182,212,0.45)] hover:shadow-[0_0_42px_rgba(6,182,212,0.7)] transition-all"
                  >
                    <Send className="h-4 w-4" />
                    {submitting ? "Sending…" : "Request Consultation"}
                  </button>
                  <p className="text-[10px] font-mono text-slate-500 text-center">
                    We reply from a real human. No spam, ever.
                  </p>
                </form>
              ) : (
                <div
                  data-testid={SERVICES.modalSuccess}
                  className="py-2 text-center space-y-5"
                >
                  <div className="mx-auto h-16 w-16 flex items-center justify-center border border-cyan-500/40 bg-cyan-500/10 animate-pulse-glow">
                    <CheckCircle2 className="h-8 w-8 text-cyan-400" />
                  </div>
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] font-bold text-cyan-400 flex items-center justify-center gap-2">
                    <Sparkles className="h-3.5 w-3.5" />
                    Consultation received
                  </span>
                  <h3 className="font-display text-2xl sm:text-3xl font-black text-white">
                    Thanks, {form.name.split(" ")[0] || "friend"}.
                  </h3>
                  <p className="font-mono text-sm text-slate-400 leading-relaxed max-w-sm mx-auto">
                    We&apos;ll review your request and reply within 1 business day.
                  </p>
                  <button
                    onClick={close}
                    className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-cyan-300 border border-cyan-400/40 hover:border-cyan-300 hover:bg-cyan-500/10 px-6 py-3 transition-colors"
                  >
                    Close
                  </button>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Field({ id, label, error, testId, ...rest }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400 mb-2"
      >
        {label}
      </label>
      <input
        id={id}
        data-testid={testId}
        className={`w-full bg-slate-900 border ${
          error ? "border-red-500/60" : "border-white/10"
        } text-white placeholder:text-slate-600 h-11 px-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-cyan-500 focus:border-cyan-500 transition-colors`}
        {...rest}
      />
      {error && <p className="mt-1.5 font-mono text-xs text-red-400">{error}</p>}
    </div>
  );
}
